<template>
  <div class="gr-root">
    <div v-if="pending" class="gr-container gr-detailstate">
      <v-progress-circular
        indeterminate
        color="green-darken-4"
        size="48"
        aria-hidden="true"
      />
      <p class="gr-detailstate__text" aria-live="polite">
        Memuat detail aktivitas...
      </p>
    </div>

    <div v-else-if="error || !activityData" class="gr-container gr-detailstate">
      <ActivityNotFound
        v-if="error?.statusCode === 404"
        :error-message="error?.message"
      />
      <template v-else>
        <div class="gr-detailstate__alert" role="alert">
          <p class="gr-detailstate__alert-title">Gagal memuat data</p>
          <p class="gr-detailstate__alert-text">
            {{ error?.message || "Terjadi kesalahan saat memuat aktivitas" }}
          </p>
        </div>
        <button
          type="button"
          class="gr-btn gr-btn--solid"
          @click="() => refresh()"
        >
          Coba Lagi
        </button>
      </template>
    </div>

    <template v-else>
      <ActivityDetailContent
        :activity="activityData"
        @register-volunteer="handleApplyVolunteer"
      />

      <!-- Apply stays within thumb reach while the page is long. Share is
               not repeated here: unlike canonical, the panel that carries it
               now renders on phones too. -->
      <ClientOnly>
        <div
          v-if="
            smAndDown &&
            !activityData.isFinished &&
            !activityData.isMine &&
            !activityData.isExpired
          "
          class="gr-detailbar"
        >
          <div class="gr-container">
            <button
              type="button"
              class="gr-btn gr-btn--solid gr-detailbar__btn"
              :disabled="activityData.isExpired"
              @click="handleApplyVolunteer"
            >
              Jadi relawan
            </button>
          </div>
        </div>
      </ClientOnly>

      <!-- Canonical component, rendered unchanged: an authenticated
               mutation with a PDF upload whose capability must survive
               exactly. See CANDIDATE-activity-detail.md. -->
      <ApplyVolunteerModal
        :open="isOpenDialogApply"
        :activity-id="activityId"
        :is-already-registered="activityData.isAlreadyRegistered"
        @close="closeApplyDialog"
        @success="onApplicationSuccess"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useDisplay } from "vuetify";
import ActivityDetailContent from "@/features/Activity/views/components/ActivityDetailContent.vue";
import ActivityNotFound from "@/features/Activity/views/components/ActivityNotFound.vue";
import ApplyVolunteerModal from "~/features/Activity/views/components/ApplyVolunteerModal.vue";
import { useActivityDetail } from "~/features/Activity/composables/useActivityDetail";

const route = useRoute();
const router = useRouter();
const activityId = computed(() => route.params.slug?.toString() || "");

// Resolved after mount so the server-rendered markup and the first client
// render agree; `useDisplay()` has no viewport to read on the server.
const display = useDisplay();
const smAndDown = ref(false);

const isOpenDialogApply = ref(false);

// The dialog takes a one-way `:open` prop plus a `@close` event rather than
// `v-model`, so Vuetify never sees the binding it uses to restore focus and
// drops it on `<body>` when the dialog closes. A keyboard user who pressed
// Escape would then have to tab from the top of the document to get back to
// what they opened (WCAG 2.4.3). Hold the trigger ourselves.
let dialogTrigger: HTMLElement | null = null;

function captureDialogTrigger(): void {
  dialogTrigger =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
}

// The composable keeps its state in plain refs rather than `useState`, so
// reusing it here cannot collide with the canonical detail route.
const { fetchActivityDetail } = useActivityDetail();

const {
  data: activityData,
  pending,
  error,
  refresh,
} = useAsyncData(
  `site:activity-${activityId.value}`,
  async () => {
    if (!activityId.value) {
      throw createError({
        statusCode: 400,
        message: "Activity ID is required",
      });
    }

    const data = await fetchActivityDetail(activityId.value).catch(
      (err: unknown) => {
        const response =
          typeof err === "object" && err !== null
            ? (Reflect.get(err, "response") as
                | { status?: number; _data?: { message?: string } }
                | undefined)
            : undefined;
        throw createError({
          statusCode: response?.status ?? 500,
          message:
            response?._data?.message ?? "Failed to load activity details",
          fatal: true,
        });
      },
    );

    if (!data) {
      throw createError({
        statusCode: 404,
        message: "Aktivitas tidak ditemukan",
      });
    }
    return data;
  },
  { server: true, lazy: false },
);

onMounted(() => {
  smAndDown.value = display.smAndDown.value;

  // A cached SSR-side fetch error otherwise replays forever for this key on
  // client navigation, and nothing else here gives it a way to recover.
  if (error.value) refresh();

  watch(
    () => display.smAndDown.value,
    (next) => {
      smAndDown.value = next;
    },
  );
});

function handleApplyVolunteer(): void {
  const { $auth } = useNuxtApp();
  if (!$auth.isAuthenticated) {
    router.push({
      path: "/auth",
      query: { redirect: route.fullPath },
    });
    return;
  }
  if (!activityData.value) return;
  captureDialogTrigger();
  isOpenDialogApply.value = true;
}

function closeApplyDialog(): void {
  isOpenDialogApply.value = false;
  const trigger = dialogTrigger;
  dialogTrigger = null;
  if (trigger) void nextTick(() => trigger.focus({ preventScroll: true }));
}

function onApplicationSuccess(): void {
  const { $toast } = useNuxtApp();
  $toast.success("Pendaftaran sebagai relawan berhasil disubmit!");
  refresh();
}

useHead(() => {
  if (!activityData.value) return {};

  const title = activityData.value.title || "Detail Aktivitas";
  const description =
    activityData.value.description?.substring(0, 160) ||
    "Detail aktivitas relawan";
  const imageUrl = activityData.value.img;
  const siteUrl = "https://gotongroyong.org";
  const pageUrl = `${siteUrl}/activities/${activityData.value.slug}`;

  return {
    title,
    titleTemplate: "%s | Gotong Royong",
    meta: [
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl },
      { property: "og:image", content: imageUrl },
      { property: "og:image:secure_url", content: imageUrl },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: title },
      { property: "og:site_name", content: "Gotong Royong" },
      { property: "og:locale", content: "id_ID" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: imageUrl },
      { itemprop: "name", content: title },
      { itemprop: "description", content: description },
      { itemprop: "image", content: imageUrl },
    ],
    link: [{ rel: "canonical", href: pageUrl }],
  };
});
</script>

<style scoped>
.gr-detailstate {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-block: clamp(3rem, 2rem + 4vw, 6rem);
  text-align: center;
}

.gr-detailstate__text {
  margin: 0;
  color: var(--gr-ink-2);
  font-size: 1rem;
}

.gr-detailstate__alert {
  width: 100%;
  max-width: 60ch;
  padding: 16px 20px;
  border: 1px solid var(--gr-danger);
  border-radius: 4px;
  background-color: rgba(179, 38, 30, 0.06);
  text-align: left;
}

.gr-detailstate__alert-title {
  margin: 0 0 4px;
  color: var(--gr-danger);
  font-size: 1rem;
  font-weight: 700;
}

.gr-detailstate__alert-text {
  margin: 0;
  color: var(--gr-ink-2);
  font-size: 0.9375rem;
  line-height: 1.6;
}

.gr-detailbar {
  position: sticky;
  bottom: 0;
  z-index: 20;
  padding-block: 12px;
  border-top: 1px solid var(--gr-line);
  background-color: rgba(250, 249, 245, 0.96);
  backdrop-filter: saturate(1.4) blur(6px);
}

.gr-detailbar__btn {
  width: 100%;
}
</style>
